/**
 * Collapsible sub-section primitive for the mindspace filter panels.
 *
 * Two sibling buttons - the header and an optional Clear - inside one flex row,
 * so both are independently focusable. `aria-expanded` / `aria-controls` let a
 * screen reader announce the collapse state. Replaces an earlier nested
 * `<span role='button'>` Clear hack, where the Clear was a descendant of the
 * toggle and so unreachable by keyboard without also toggling.
 *
 * Lived at the bottom of `SourceLessonFilter.tsx` and was imported from there
 * by the three sibling filters - a working but misleading arrangement, since
 * nothing about it is about source lessons.
 *
 * **In the phone sheet** (`useFilterLayout() === 'sheet'`) it is not
 * collapsible: a mono label (with how many are on), a Clear, and the body,
 * always open. Collapsed sections in a sheet cost a tap each and hid what
 * was selected inside them.
 */

import { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { useFilterLayout } from '@/components/features/mindspace/filters/filterLayout';

export interface FilterSectionProps {
  icon: React.ReactNode;
  label: string;
  count?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClear?: () => void;
  children: React.ReactNode;
}

export function FilterSection({
  icon,
  label,
  count,
  open,
  onOpenChange,
  onClear,
  children,
}: FilterSectionProps) {
  const contentId = useId();
  const layout = useFilterLayout();
  if (layout === 'sheet') {
    return (
      <SheetSection
        id={contentId}
        label={label}
        count={count}
        onClear={onClear}
      >
        {children}
      </SheetSection>
    );
  }
  return (
    <div className='border-b border-border'>
      <div className='flex items-stretch'>
        <button
          type='button'
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-controls={contentId}
          className='flex flex-1 items-center justify-between gap-2 px-4 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/50 focus-visible:bg-muted/40 focus-visible:outline-none'
        >
          <span className='inline-flex items-center gap-1.5'>
            <span className='text-muted-foreground'>{icon}</span>
            {label}
            {count != null && count > 0 && (
              <span className='dark:tint-brand inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-50 px-1 text-xs font-medium text-brand-700'>
                {count}
              </span>
            )}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {onClear && (
          <button
            type='button'
            onClick={onClear}
            aria-label={`Clear ${label} filter`}
            className='flex-shrink-0 border-l border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:bg-muted/40 focus-visible:outline-none'
          >
            Clear
          </button>
        )}
      </div>
      {open && (
        <div id={contentId} className='px-4 pb-3'>
          {children}
        </div>
      )}
    </div>
  );
}

/** The sheet form: a mono label (with how many are on), a Clear, the body. */
function SheetSection({
  id,
  label,
  count,
  onClear,
  children,
}: Pick<FilterSectionProps, 'label' | 'count' | 'onClear' | 'children'> & {
  id: string;
}) {
  return (
    <section
      aria-labelledby={id}
      className='border-b border-border py-3 last:border-b-0'
    >
      <div className='mb-1 flex min-h-11 items-center justify-between gap-3'>
        <h3
          id={id}
          className='font-mono text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground'
        >
          {label}
          {count != null && count > 0 && (
            <span className='text-foreground'> · {count}</span>
          )}
        </h3>
        {onClear && (
          <button
            type='button'
            onClick={onClear}
            aria-label={`Clear ${label} filter`}
            className='tap-target px-1 text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          >
            Clear
          </button>
        )}
      </div>
      {children}
    </section>
  );
}
